import React, { useState, useEffect } from 'react';
import { PieChart as LucidePieChart, List, Target, ArrowRightLeft, TrendingUp, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { MacroState, Holding, OptimizationData } from '../types';

interface CardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function PortfolioCard({ title, icon, children }: CardProps) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-widest text-white/40 flex items-center gap-2">
          {icon}
          {title}
        </h3>
      </div>
      <div className="min-h-[100px] flex flex-col justify-center">
        {children}
      </div>
    </div>
  );
}

export function HoldingsWeightPie({ holdings, language = 'ko', height = 200 }: { holdings: Holding[], language?: string, height?: number }) {
  const isKo = language === 'ko';
  const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#10b981', '#3b82f6'];

  const data = holdings
    .filter(h => (h.weight || 0) > 0)
    .sort((a, b) => (b.weight || 0) - (a.weight || 0))
    .map((h, i) => ({
      name: h.symbol,
      value: h.weight || 0,
      color: COLORS[i % COLORS.length]
    }));

  if (data.length === 0) return null;

  return (
    <div className="w-full h-full" style={{ minHeight: height }}>
      <ResponsiveContainer width="100%" height={height} minWidth={0} minHeight={0}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            innerRadius={45}
            outerRadius={65}
            paddingAngle={5}
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color} 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth={1}
              />
            ))}
          </Pie>
          <Legend 
            verticalAlign="bottom" 
            align="center"
            height={50}
            iconType="circle"
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value, entry: any) => (
              <span className="text-[9px] text-white/50 font-bold ml-1 uppercase">
                {value} <span className="text-white/20 ml-1">{(entry.payload.value * 100).toFixed(1)}%</span>
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

interface AssetAllocationCardProps {
  holdings: Holding[];
  language?: string;
  className?: string;
}

export function AssetAllocationCard({ holdings, language = 'ko', className = '' }: AssetAllocationCardProps) {
  const isKo = language === 'ko';
  return (
    <div className={`bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4 flex flex-col ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-widest text-white/40 flex items-center gap-2">
          <LucidePieChart className="w-4 h-4 text-indigo-400" />
          {isKo ? '자산 배분 현황' : 'Asset Allocation Status'}
        </h3>
      </div>
      <div className="flex-1 flex flex-col justify-center min-h-[200px]">
        {holdings.length > 0 ? (
          <HoldingsWeightPie holdings={holdings} language={language} height={250} />
        ) : (
          <div className="text-center space-y-2">
            <LucidePieChart className="w-8 h-8 text-white/10 mx-auto" />
            <p className="text-[10px] text-white/20 uppercase font-bold">{isKo ? '자산 내역 없음' : 'No Assets'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function NewBuyRecommendationsCard({ optimizationData, language = 'ko', marketMode = 'US' }: { optimizationData?: any, language?: string, marketMode?: string }) {
  const isKo = language === 'ko';
  const isKR = marketMode === 'KR';
  const [liveRecs, setLiveRecs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (optimizationData?.newRecommendations && Array.isArray(optimizationData.newRecommendations) && optimizationData.newRecommendations.length > 0) {
      setLiveRecs(optimizationData.newRecommendations);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    fetch(`/api/strategy/recommendations?market=${marketMode}&limit=5`)
      .then(res => res.ok ? res.json() : [])
      .then((data: any[]) => {
        if (!isMounted) return;
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map(item => {
            const curPrice = Number(item.currentPrice || 0);
            const entryPriceNum = Number(item.targetBuyPrice || (curPrice > 0 ? curPrice * 0.98 : 0));
            const entryPriceFormatted = isKR 
              ? (entryPriceNum > 0 ? `₩${Math.round(entryPriceNum).toLocaleString()}` : 'N/A')
              : (entryPriceNum > 0 ? `$${entryPriceNum.toFixed(2)}` : 'N/A');

            let rating = 'POSITIVE';
            if (item.overallScore >= 80) rating = 'HIGH MOMENTUM';
            else if (item.overallScore >= 65) rating = 'POSITIVE';
            else rating = 'WATCH';

            return {
              symbol: item.symbol,
              name: item.name || item.symbol,
              entryPrice: entryPriceFormatted,
              targetWeight: Math.round(100 / Math.max(1, data.length)),
              rating
            };
          });
          setLiveRecs(mapped);
        } else {
          setLiveRecs([]);
        }
      })
      .catch(err => {
        console.error("Failed to load quant recommendations for NewBuyRecommendationsCard:", err);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => { isMounted = false; };
  }, [optimizationData, marketMode, isKR]);

  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-widest text-white/40 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          {isKo ? '퀀트 팩터 상위 스크리닝 종목' : 'Top Quant Factor Screened Assets'}
          <div className="group relative ml-1">
            <AlertCircle className="w-3 h-3 text-white/20 cursor-help" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg text-[9px] text-white/60 font-bold opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-50 shadow-2xl leading-relaxed">
              {isKo ? '현재 시장 데이터와 팩터 모델을 기반으로 통계적 모멘텀이 포착된 스크리닝 종목입니다.' : 'Assets screened based on quantitative factor models and statistical momentum.'}
            </div>
          </div>
        </h3>
      </div>

      <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[500px]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-white/20">
            <RefreshCw className="w-6 h-6 animate-spin mb-3 text-emerald-400" />
            <p className="text-xs uppercase tracking-widest font-mono italic">{isKo ? '퀀트 스크리닝 데이터 로딩 중...' : 'LOADING QUANT SCREEN...'}</p>
          </div>
        ) : liveRecs.length > 0 ? (
          liveRecs.map((rec: any, i: number) => (
            <div key={i} className="group relative overflow-hidden bg-white/[0.02] border border-white/5 rounded-2xl p-4 hover:bg-white/[0.05] transition-all">
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 font-black text-xs">
                    {rec.symbol[0]}
                  </div>
                  <div>
                    <div className="text-sm font-black text-white">{rec.symbol}</div>
                    <div className="text-[10px] text-white/20 font-bold uppercase truncate max-w-[120px]">{rec.name || 'Equity Asset'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono font-bold text-emerald-400">{rec.targetWeight}%</div>
                  <div className="text-[9px] text-white/20 font-black uppercase">Model Weight</div>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
                <div>
                  <div className="text-[9px] text-white/20 font-black uppercase mb-1 tracking-widest">{isKo ? '기준 가격' : 'Base Price'}</div>
                  <div className="text-xs font-mono font-bold text-white/80">{rec.entryPrice}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] text-white/20 font-black uppercase mb-1 tracking-widest">{isKo ? '모멘텀 지수' : 'Momentum Index'}</div>
                  <div className="text-xs font-black text-emerald-500 uppercase tracking-tighter">
                    {rec.rating === 'STRONG BUY'
                      ? 'HIGH MOMENTUM'
                      : rec.rating === 'BUY'
                      ? 'POSITIVE'
                      : rec.rating === 'ACCUMULATE'
                      ? 'WATCH'
                      : (rec.rating || 'NEUTRAL')}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center text-white/30">
            <p className="text-xs font-medium">{isKo ? '현재 조건에 부합하는 퀀트 스크리닝 종목이 없습니다.' : 'No quant screened assets available.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
